import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogContent, Button, IconButton, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import SendIcon from '@mui/icons-material/Send'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import hipersendService from '../../../services/hipersend'
import Text from '../../Text'
import {
  buildSendSnippet, describeSnippetVariables, describeApiError, STATUS_COLORS,
  IDIOMA_DO_ENVIO, nomeDoIdioma, idiomaSaiNoEnvio
} from '../../../libs/metaTemplates'

/**
 * Dialog "Como usar este template": pega um modelo APROVADO e mostra, prontinho:
 *  - o snippet de metatags que o ERP manda em /messages/sendMessage (com botão
 *    de copiar);
 *  - a tabela do que entra em cada variável {{n}} (describeSnippetVariables);
 *  - um "Testar envio" que dispara a mensagem de exemplo. ATENÇÃO: o envio é
 *    REAL — por isso exige um número e uma confirmação explícita antes de sair.
 *
 * O snippet já vem com valores de exemplo nas variáveis, então é o mesmo texto
 * que testamos e que o cliente copia (só troca os exemplos pelos dados reais no
 * uso do dia a dia).
 */
function HowToUseTemplateDialog({ open, onClose, api, secret, item }) {
  const [phone, setPhone] = useState('')
  const [copied, setCopied] = useState(false)
  // fluxo do teste: ocioso -> aguardando confirmação -> enviando
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState(null) // { ok, message }

  const snippet = useMemo(() => (item ? buildSendSnippet(item) : ''), [item])
  const variables = useMemo(() => (item ? describeSnippetVariables(item) : []), [item])

  // Zera o estado do teste toda vez que abre (ou troca de modelo) — nunca deixa
  // um número/resultado antigo de outro template pendurado na tela.
  useEffect(() => {
    if (!open) return
    setPhone('')
    setCopied(false)
    setConfirming(false)
    setSending(false)
    setSendResult(null)
  }, [open, item])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setCopied(false)
    }
  }, [snippet])

  const handleAskConfirm = useCallback(() => {
    setSendResult(null)
    setConfirming(true)
  }, [])

  const handleConfirmSend = useCallback(async () => {
    setSending(true)
    setSendResult(null)
    try {
      await hipersendService.notificacoesInteligentes.sendMessage(api, secret, {
        message: snippet,
        phone: phone.trim(),
        // fileUrl vazio é OBRIGATÓRIO: sem ele o encodeMessage do service cai no
        // ramo de anexo e estoura em fileUrl.split(...) (undefined). Este teste
        // nunca manda arquivo, então '' faz o service curto-circuitar e enviar só
        // o texto do snippet — mesmo padrão do MessageSender.
        fileUrl: '',
      })
      setSendResult({ ok: true, message: `Mensagem de teste enviada para ${phone.trim()}.` })
      setConfirming(false)
    } catch (err) {
      setSendResult({ ok: false, message: describeApiError(err) })
      setConfirming(false)
    } finally {
      setSending(false)
    }
  }, [api, secret, snippet, phone])

  if (!item) return null

  const phoneFilled = phone.trim().length > 0

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <div className="flex items-center justify-between px-5 py-4">
        <div>
          <Text className="text-lg font-black">Como usar este template</Text>
          <Text className="text-xs font-light opacity-70">
            Modelo <b>{item.name}</b> — pronto para o seu sistema disparar
          </Text>
        </div>
        <IconButton size="small" onClick={onClose} disabled={sending}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent dividers>
        {/* O snippet só leva o nome do modelo e o envio completa com pt_BR:
            modelo em outro idioma é recusado pela Meta em todo disparo. */}
        {!idiomaSaiNoEnvio(item) && (
          <div
            className="flex items-start gap-2 p-3 rounded-md mb-5"
            style={{ backgroundColor: `${STATUS_COLORS.yellow}14`, borderLeft: `3px solid ${STATUS_COLORS.yellow}` }}
          >
            <WarningAmberIcon style={{ fontSize: 18, color: STATUS_COLORS.yellow, marginTop: 2 }} />
            <Text className="text-xs font-bold leading-relaxed" style={{ color: STATUS_COLORS.yellow }}>
              Este modelo está em {nomeDoIdioma(item.language)}. Hoje os envios saem em
              {' '}{nomeDoIdioma(IDIOMA_DO_ENVIO)}, então a Meta recusa a mensagem — para usar
              este conteúdo, crie o modelo de novo em {nomeDoIdioma(IDIOMA_DO_ENVIO)}.
            </Text>
          </div>
        )}

        {/* Snippet de disparo */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <Text className="text-xs font-bold uppercase opacity-70">
              O que o seu sistema envia
            </Text>
            <Button
              size="small"
              variant="outlined"
              startIcon={copied ? <CheckIcon style={{ fontSize: 15 }} /> : <ContentCopyIcon style={{ fontSize: 15 }} />}
              onClick={handleCopy}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <div
            className="rounded-md p-3 overflow-x-auto"
            style={{ backgroundColor: 'rgba(128,128,128,0.10)', border: '1px solid rgba(128,128,128,0.18)' }}
          >
            <code
              className="text-xs leading-relaxed whitespace-pre-wrap break-all"
              style={{ fontFamily: 'monospace' }}
            >
              {snippet}
            </code>
          </div>
          <Text className="text-[11px] font-light opacity-60 mt-2 leading-snug">
            Cole este conteúdo no campo <b>message</b> da sua chamada de envio. Cada
            trecho <b>%VariableNumeric%</b> é uma variável do corpo, na ordem — troque os
            exemplos abaixo pelos dados reais do cliente.
          </Text>
        </div>

        {/* Tabela de variáveis */}
        {variables.length > 0 && (
          <div className="mb-5">
            <Text className="text-xs font-bold uppercase opacity-70 mb-2">
              O que entra em cada variável
            </Text>
            <div className="overflow-x-auto rounded-md" style={{ border: '1px solid rgba(128,128,128,0.15)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><Text className="text-xs font-bold uppercase opacity-70">Posição</Text></TableCell>
                    <TableCell><Text className="text-xs font-bold uppercase opacity-70">Variável</Text></TableCell>
                    <TableCell><Text className="text-xs font-bold uppercase opacity-70">O que colocar aqui</Text></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variables.map((v) => (
                    <TableRow key={v.n}>
                      <TableCell><Text className="text-sm font-medium">{v.n}º</Text></TableCell>
                      <TableCell>
                        <code className="text-xs" style={{ fontFamily: 'monospace' }}>{`{{${v.n}}}`}</code>
                      </TableCell>
                      <TableCell><Text className="text-sm font-light">{v.hint}</Text></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Testar envio (envio REAL) */}
        <div>
          <Text className="text-xs font-bold uppercase opacity-70 mb-2">Testar envio</Text>

          <div
            className="flex items-start gap-2 p-3 rounded-md mb-3"
            style={{ backgroundColor: `${STATUS_COLORS.red}12`, borderLeft: `3px solid ${STATUS_COLORS.red}` }}
          >
            <WarningAmberIcon style={{ fontSize: 18, color: STATUS_COLORS.red, marginTop: 2 }} />
            <Text className="text-xs font-bold leading-relaxed" style={{ color: STATUS_COLORS.red }}>
              Atenção: este envio é REAL — dispara um WhatsApp de verdade, cobrado
              normalmente. Envie apenas para o SEU próprio número, nunca para o número
              de um cliente.
            </Text>
          </div>

          <div className="flex items-start gap-2 flex-wrap">
            <TextField
              size="small"
              label="Seu número (com DDD)"
              placeholder="5599999999999"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setConfirming(false) }}
              disabled={sending}
              sx={{ minWidth: 220, flex: 1 }}
            />
            {!confirming ? (
              <Button
                variant="contained"
                startIcon={<SendIcon style={{ fontSize: 15 }} />}
                onClick={handleAskConfirm}
                disabled={!phoneFilled || sending}
              >
                Testar envio
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button size="small" onClick={() => setConfirming(false)} disabled={sending}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon style={{ fontSize: 15 }} />}
                  onClick={handleConfirmSend}
                  disabled={sending}
                >
                  {sending ? 'Enviando...' : 'Confirmar envio real'}
                </Button>
              </div>
            )}
          </div>

          {confirming && !sending && (
            <Text className="text-xs font-bold mt-2" style={{ color: STATUS_COLORS.red }}>
              Vou disparar um WhatsApp real para {phone.trim()}. Confirma?
            </Text>
          )}

          {sendResult && (
            <div
              className="flex items-start gap-1.5 p-3 rounded-md mt-3"
              style={{
                backgroundColor: `${sendResult.ok ? STATUS_COLORS.green : STATUS_COLORS.red}12`,
                borderLeft: `3px solid ${sendResult.ok ? STATUS_COLORS.green : STATUS_COLORS.red}`,
              }}
            >
              {sendResult.ok
                ? <CheckCircleIcon style={{ fontSize: 15, color: STATUS_COLORS.green, marginTop: 2 }} />
                : <ErrorOutlineIcon style={{ fontSize: 15, color: STATUS_COLORS.red, marginTop: 2 }} />}
              <Text
                className="text-xs font-light"
                style={{ color: sendResult.ok ? STATUS_COLORS.green : STATUS_COLORS.red }}
              >
                {sendResult.message}
              </Text>
            </div>
          )}
        </div>
      </DialogContent>

      <div className="flex items-center justify-end gap-2 px-5 py-3">
        <Button size="small" onClick={onClose} disabled={sending}>
          Fechar
        </Button>
      </div>
    </Dialog>
  )
}

export default HowToUseTemplateDialog
